import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import HeroSkeleton from "./skeletons/HeroSkeleton";

interface GameSlide {
  id: string;
  title: string;
  hero_tagline: string | null;
  hero_image_url: string | null;
  hero_small_image_url: string | null;
  in_development: boolean;
  playtesting: boolean;
}

const SLIDE_DURATION = 8000; // 8 seconds per slide

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [games, setGames] = useState<GameSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const reduceMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const autoplay = useRef(Autoplay({ delay: SLIDE_DURATION, stopOnInteraction: false })).current;
  const [carouselRef, carouselApi] = useEmblaCarousel({ loop: true, duration: 30 }, [autoplay]);

  useEffect(() => {
    const fetchGames = async () => {
      const { data, error } = await supabase
        .from("games")
        .select("id, title, hero_tagline, hero_image_url, hero_small_image_url, in_development, playtesting")
        .eq("is_published", true)
        .eq("featured", true)
        .order("sort_order", { ascending: true });

      if (!error && data) {
        setGames(data);
      }
      setLoading(false);
    };

    fetchGames();
  }, []);

  useEffect(() => {
    if (!carouselApi) return;

    const updateCurrentSlide = () => setCurrentSlide(carouselApi.selectedScrollSnap());
    updateCurrentSlide();
    carouselApi.on("select", updateCurrentSlide);
    carouselApi.on("reInit", updateCurrentSlide);

    return () => {
      carouselApi.off("select", updateCurrentSlide);
      carouselApi.off("reInit", updateCurrentSlide);
    };
  }, [carouselApi]);

  useEffect(() => {
    carouselApi?.reInit();
  }, [carouselApi, games]);

  const scrollPrevious = useCallback(() => {
    carouselApi?.scrollPrev();
    autoplay.reset();
  }, [carouselApi, autoplay]);

  const scrollNext = useCallback(() => {
    carouselApi?.scrollNext();
    autoplay.reset();
  }, [carouselApi, autoplay]);

  const goToSlide = useCallback(
    (index: number) => {
      carouselApi?.scrollTo(index);
      autoplay.reset();
    },
    [carouselApi, autoplay],
  );

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };

  if (loading) {
    return <HeroSkeleton />;
  }

  if (games.length === 0) {
    return (
      <section className="relative h-[100dvh] lg:h-[700px] w-full overflow-hidden bg-dark pt-[200px]">
        <h1 className="sr-only">Rogue Skull Games — Indie Tabletop Publisher</h1>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-dark-foreground">No games available</div>
        </div>
      </section>
    );
  }

  const currentGame = games[currentSlide];

  return (
    <section ref={sectionRef} className="relative h-[100dvh] lg:h-[780px] w-full overflow-hidden section-dark">
      {/* Background carousel track */}
      <div ref={carouselRef} className="absolute inset-0 overflow-hidden">
        <div className="flex h-full touch-pan-y">
          {games.map((game, index) => (
            <div key={game.id} className="relative h-full min-w-0 shrink-0 grow-0 basis-full">
              {game.hero_image_url && (
                <img
                  src={game.hero_image_url}
                  alt={game.title}
                  className="h-full w-full object-cover brightness-[1]"
                  width={1920}
                  height={1080}
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "auto"}
                  decoding={index === 0 ? "sync" : "async"}
                />
              )}
              {/* <div className="hero-overlay hero-overlay-home" /> */}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom fade so the slider blends into the dark section below
      <div className="hero-fade-bottom pointer-events-none" aria-hidden="true" /> */}

      {/* Angle fade so the slider blends into the dark section below */}
      <div className="hero-fade-angle pointer-events-none" aria-hidden="true" />

      {/* Content Layer - Single instance that updates */}
      <div className="container hero-content-home z-20 justify-center">
        <div className="flex w-full flex-col gap-0 md:flex-row md:items-center md:gap-10 lg:gap-16">
          {currentGame.hero_small_image_url && (
            <div className="flex w-full shrink-0 justify-center pt-12 md:pt-10 md:w-[40%] md:justify-center">
              <img
                key={`small-image-${currentSlide}`}
                src={currentGame.hero_small_image_url}
                alt={`${currentGame.title} artwork`}
                className="h-auto max-h-[300px] w-auto max-w-full object-contain md:max-h-[380px] lg:max-h-[480px]"
                loading="eager"
                decoding="async"
              />
            </div>
          )}
          <div className={`relative z-10 min-w-0 flex-1 ${currentGame.hero_small_image_url ? "-mt-12 md:mt-0" : ""}`}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={`label-${currentGame.id}`}
                className="section-label"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: reduceMotion ? 0 : 0.01 } }}
                exit={{
                  opacity: 0,
                  transition: { duration: reduceMotion ? 0 : 0.24, ease: "easeIn" },
                }}
              >
                {currentGame.title}
              </motion.span>
              <motion.h1
                key={currentGame.id}
                style={{ lineHeight: 1.1 }}
                className="text-5xl md:text-6xl lg:text-7xl text-dark-foreground font-display font-bold tracking-widest mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: reduceMotion ? 0 : 0.01 } }}
                exit={{
                  opacity: 0,
                  y: reduceMotion ? 0 : -8,
                  transition: { duration: reduceMotion ? 0 : 0.24, ease: "easeIn" },
                }}
              >
                {(currentGame.hero_tagline || currentGame.title)
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((word, index) => (
                    <motion.span
                      key={index}
                      className="inline-block"
                      initial={{ opacity: 0, y: reduceMotion ? 0 : 14 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: reduceMotion ? 0 : 0.32,
                          delay: reduceMotion ? 0 : 0.05 + index * 0.07,
                          ease: "easeOut",
                        },
                      }}
                    >
                      {word}
                      {index < (currentGame.hero_tagline || currentGame.title).split(/\s+/).filter(Boolean).length - 1
                        ? "\u00A0"
                        : ""}
                    </motion.span>
                  ))}
                <span className="sr-only"> — Rogue Skull Games, indie tabletop board game publisher</span>
              </motion.h1>
            </AnimatePresence>
            <Link
              to={`/games/${generateSlug(currentGame.title)}`}
              className="btn-outline-light inline-flex items-center gap-3 group"
            >
              Discover {currentGame.title}
              <svg
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Slide controls — indicators bottom center, arrows at the container edges */}
      {games.length > 1 && (
        <>
          {/* Arrows aligned to the shared container edges, vertically centered on the indicator lines */}
          <div className="absolute inset-x-0 bottom-8 z-30">
            <div className="container relative">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={scrollPrevious}
                className="absolute -bottom-5 left-10 h-11 w-11 shrink-0 bg-transparent p-0 text-dark-foreground/70 transition-none hover:bg-transparent hover:text-dark-foreground"
                aria-label="Previous featured game"
              >
                <svg className="!h-8 !w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8l-4 4m0 0l4 4m-4-4h18" />
                </svg>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={scrollNext}
                className="absolute -bottom-5 right-10 h-11 w-11 shrink-0 bg-transparent p-0 text-dark-foreground/70 transition-none hover:bg-transparent hover:text-dark-foreground"
                aria-label="Next featured game"
              >
                <svg className="!h-8 !w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Button>
            </div>
          </div>
          {/* Indicator lines stay bottom center */}
          <div
            className="absolute bottom-8 left-1/2 z-30 flex -translate-x-1/2 items-center justify-center gap-2 sm:gap-3"
            aria-label="Featured games controls"
          >
            {games.map((game, index) => (
              <Button
                key={game.id}
                type="button"
                variant="ghost"
                onClick={() => goToSlide(index)}
                className={`hero-indicator h-[4px] p-0 transition-none hover:bg-dark-foreground/40 ${index === currentSlide ? "active" : ""}`}
                aria-label={`Go to ${game.title}`}
                aria-current={index === currentSlide ? "true" : undefined}
              />
            ))}
          </div>
        </>
      )}

      {/* Slide Counter - Clickable Numbers
      {games.length > 1 && (
        <div className="absolute bottom-8 right-6 md:right-12 z-30 font-mono tracking-wider flex items-center">
          {games.map((_, index) => (
            <span key={index} className="flex items-center">
              <button
                type="button"
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`transition-colors duration-300 ${
                  index === currentSlide ? "text-dark-foreground" : "text-dark-foreground/40 hover:text-blood-red"
                }`}
              >
                {String(index + 1).padStart(2, "0")}
              </button>
              {index < games.length - 1 && <span className="mx-2 text-dark-foreground/40">/</span>}
            </span>
          ))}
        </div>
      )} */}
    </section>
  );
};

export default HeroSlider;
